import { Stack } from "@mui/material";
import React, { useMemo } from "react";
import { useTheme } from "@mui/material";
import { Button, GlobeIcon } from "components";
import { ActionButtonType } from "../types";
import { KbFileDetailResponse } from "shared";

type ButtonKey = "unpublish" | "delete" | "restore" | "draft" | "publish";

interface ButtonConfig {
  variant: "primary" | "secondary";
  label: string;
  action: ActionButtonType;
  icon?: React.ComponentType;
}

const BUTTON_CONFIGS: Record<ButtonKey, ButtonConfig> = {
  unpublish: { variant: "secondary", label: "Unpublish", action: "unpublish" },
  delete: { variant: "secondary", label: "Delete", action: "delete_version" },
  restore: {
    variant: "primary",
    label: "Restore",
    action: "restore",
    icon: GlobeIcon,
  },
  draft: { variant: "secondary", label: "Save as Draft", action: "draft" },
  publish: {
    variant: "primary",
    label: "Publish",
    action: "publish",
    icon: GlobeIcon,
  },
};

interface ButtonGroupProps {
  onActionButtonClick: (type: ActionButtonType) => void;
  isPrimaryActionLoading: boolean;
  isSecondaryActionLoading: boolean;
  mode: "edit" | "view";
  tabValue?: number;
  kbFileDetails: KbFileDetailResponse;
  isLiveVersion: boolean;
}

function getButtonSet({
  tabValue,
  isDraft,
  isPublicAndLive,
  isLiveVersion,
  mode,
  visibility,
}: {
  tabValue?: number;
  isDraft: boolean;
  isPublicAndLive: boolean;
  isLiveVersion: boolean;
  mode: "edit" | "view";
  visibility?: string;
}): ButtonKey[] {
  // Content tab (tabValue === 0 or undefined)
  if (mode === "edit") return ["draft", "publish"];

  // View mode
  if (isPublicAndLive) return ["unpublish"];
  if (visibility === "public" && !isLiveVersion) return ["delete", "restore"];
  // Version History tab (tabValue === 1)
  if (tabValue === 1) {
    if (isDraft) return ["delete", "publish"];
    if (isPublicAndLive) return ["unpublish"];
    return ["delete", "restore"];
  }

  return ["publish"];
}

const ButtonGroup = ({
  onActionButtonClick,
  isPrimaryActionLoading,
  isSecondaryActionLoading,
  mode,
  tabValue,
  kbFileDetails,
  isLiveVersion,
}: ButtonGroupProps) => {
  const isDraft =
    kbFileDetails?.record_visibility === "draft" &&
    kbFileDetails?.version === 0;
  const isPublicAndLive =
    kbFileDetails?.record_visibility === "public" && isLiveVersion;
  const isAnyLoading = isPrimaryActionLoading || isSecondaryActionLoading;

  const buttonSet = useMemo(
    () =>
      getButtonSet({
        tabValue,
        isDraft,
        isPublicAndLive,
        isLiveVersion,
        mode,
        visibility: kbFileDetails?.record_visibility,
      }),
    [tabValue, isDraft, isPublicAndLive, isLiveVersion, mode, kbFileDetails],
  );

  if (buttonSet.length === 0) return null;

  return (
    <>
      {buttonSet.map((key, index) => {
        const config = BUTTON_CONFIGS[key];
        const isLastButton = index === buttonSet.length - 1;
        const isDisabled =
          isAnyLoading || (key === "delete" && kbFileDetails?.is_live);

        return (
          <Button
            key={key}
            fullWidth
            type="button"
            variant={config.variant}
            startIcon={config.icon ? <config.icon /> : undefined}
            onClick={() => onActionButtonClick(config.action)}
            disabled={isDisabled}
            loading={
              isLastButton ? isPrimaryActionLoading : isSecondaryActionLoading
            }
          >
            {config.label}
          </Button>
        );
      })}
    </>
  );
};

export const KbFormBottomButtons = ({
  onActionButtonClick,
  isPrimaryActionLoading,
  isSecondaryActionLoading,
  mode,
  tabValue,
  kbFileDetails,
  isLiveVersion,
}: ButtonGroupProps) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        justifyContent: "flex-end",
        gap: "8px",
        mt: "auto",
        p: "8px 16px",
        borderTop: `1px solid ${theme.palette.neutral["015"]}`,
      }}
    >
      <ButtonGroup
        onActionButtonClick={onActionButtonClick}
        isPrimaryActionLoading={isPrimaryActionLoading}
        isSecondaryActionLoading={isSecondaryActionLoading}
        mode={mode}
        tabValue={tabValue}
        kbFileDetails={kbFileDetails}
        isLiveVersion={isLiveVersion}
      />
    </Stack>
  );
};
